import * as moment from 'moment';
import {betragRound} from "../common/utils";
import {
  beginEndDateToString,
  firebaseObjectToArray,
  firebaseObjectToObjectWithKey,
  getFromArrayChecked,
  getFromMapChecked, stringTrimmed,
  valueOrDefault,
  valueOrEmptyObject,
  valueOrNull,
  valueOrZero
} from '../utils/tools';
import {config} from './config';
const assert = require('assert');


// TODO anonymous signIn für weitere Person verwenden?
export class Person {
  $key: string;
  vorname = '';
  nachname = '';
  geburtsdatum: Date = null;
  geburtsland = '';
  nationalitaet = '';
  handy = '';

  constructor(key: string, json?: any) {
    if (json) {
      this.fromJson(json);
    }
    this.$key = key;
  }

  protected fromJson(json: any) {
    for (const key of Object.keys(valueOrEmptyObject(json))) {
      this[key] = json[key];
    }
    this.geburtsdatum = this.geburtsdatum ? new Date(this.geburtsdatum) : null;
    this.handy = Person.telefonNummerMitVorwahl(this.handy);
  }

  toJson() {
    const geburtsdatum = moment(this.geburtsdatum);
    return {
      vorname: stringTrimmed(this.vorname),
      nachname: stringTrimmed(this.nachname),
      geburtsdatum: geburtsdatum && geburtsdatum.isValid() ? geburtsdatum.format('YYYY-MM-DD') : null,
      geburtsland: stringTrimmed(this.geburtsland),
      nationalitaet: stringTrimmed(this.nationalitaet),
      handy: Person.telefonNummerMitVorwahl(this.handy)
    };
  }

  name(): string {
    return this.vorname + ' ' + this.nachname;
  }

  static telefonNummerMitVorwahl(nummer: string): string {
    if (!nummer)
      return null;
    // remove non ASCII
    let x = stringTrimmed(nummer).replace(/[^\x00-\x7F]/g, "").replaceAll(" ", "").replaceAll("/", "");
    if (x.startsWith("+"))
      return x; // Ländervorwahl vorhanden
    if (x.startsWith("00"))
      return `+${x.slice(2)}`;
    if (x.startsWith("0"))
      return `+49${x.slice(1)}`;
    if (x.startsWith("491"))
      return `+${x}`;
    if (x.startsWith("1"))
      return `+49${x}`;
    return x; // keine gültige Vorwahl
  }
}

export interface StammdatenJson  {
  uid?: string;
  displayName?: string;
  email?: string;
}

export class Stammdaten extends Person {
  readonly uid: string;
  displayName: string;
  strasse: string = null;
  plz: number = null;
  ort: string = null;
  email: string = null;
  telefon: string = null;
  readonlyName = false;

  constructor(uid: string, json?: any) {
    super(uid, json);
    this.uid = uid;
    this.telefon = Person.telefonNummerMitVorwahl(this.telefon);
  }

  name(): string {
    if (this.vorname || this.nachname) {
      return this.vorname + ' ' + this.nachname;
    } else if (this.displayName) {
      return this.displayName;
    }
    return this.email ? this.email : this.uid;
  }

  toJson() {
    const json: any = super.toJson();
    json.displayName = stringTrimmed(this.displayName);
    json.strasse = stringTrimmed(this.strasse);
    json.plz = valueOrNull(this.plz);
    json.ort = stringTrimmed(this.ort);
    json.email = stringTrimmed(this.email);
    json.telefon = Person.telefonNummerMitVorwahl(this.telefon);
    return json;
  }
}

export interface NamePreis {
  $key: string;
  name: string;
  preis: number;
}

export interface Kategorie extends NamePreis {
  capacity: Capacity;
  erw: boolean;
  jgd: boolean;
  kind: boolean;
  // min Geburtsjahr für Jugendpreis
  jgd_jahr?: number;
  hidden?: boolean;
}

export type Kurs = NamePreis;

export interface Zustieg {
  $key: string;
  abk: string;
  ort: string;
  zeit: string;
}

// Status einer einzelnen Reise, wichtig für Plätze
export let BuchungStates = {
  anfrage: 'anfrage', // intern, processBuchungenAnfrage() -> abgelehntNichtBuchbar/warteliste/angenommen
  abgelehntNichtBuchbar: 'abgelehntNichtBuchbar',
  abgelehntAusgebucht: 'abgelehntAusgebucht',
  warteliste: 'warteliste',
  angenommen: 'angenommen', // automatisch wenn Plätze frei, Zahlung ausstehend
  gebucht: 'gebucht', // angenommen und angezahlt/bezahlt
  teilgenommen: 'teilgenommen', // Reise durchgeführt und Kunde hat teilgenommen
  storniert: 'storniert', // storniert
  abgesagt: 'abgesagt', // Reise abgesagt
  abgesagtCorona: 'abgesagt (Coronavirus)',
};

export class Capacity {
  readonly $key: string;
  name: string;
  buchbar = false;
  gesamt = 0; // 50 bei einem Bus
  geblockt = 0; // Geblockt, z.B. 20 pro Bus für größere Gruppen
  kommentar: string = null;
  // Statistiken über alle Buchungen:
  angenommen = 0;
  gebucht = 0;
  warteliste = 0;

  static combinedKey(reise: Reise, kategorieCapacityName: any) {
    return kategorieCapacityName ? `${reise.$key}__${kategorieCapacityName}` : reise.$key;
  }
  static cacheKeys() {
    return ['angenommen', 'gebucht', 'warteliste'];
  }
  static hasCache(key: string): boolean {
    return Capacity.cacheKeys().indexOf(key) > -1;
  }

  constructor($key: string, json?: any) {
    if (json) {
      this.fromJson(json);
    }
    this.$key = $key;
  }
  private fromJson(json: any) {
    Object.assign(this, json);
  }
  toJsonStr() {
    return JSON.stringify(this, (key, value) => {
      if (key === '$key' || key === 'backupInstance') {
        return undefined;
      }
      return value;
    });
  }
  cacheValues() {
    const subset = {};
    Capacity.cacheKeys().forEach(key => subset[key] = this[key]);
    return subset;
  }
  constValues() {
    const subset = {};
    Object.assign(subset, this);
    delete subset['$key'];
    delete subset['backupInstance'];
    Capacity.cacheKeys().forEach(key => delete subset[key]);
    return subset;
  }

  // might be negative!
  get frei() {
    return this.gesamt - this.gebucht - this.angenommen - this.geblockt - this.warteliste;
  }
  get min() {
    return this.gebucht;
  }
  get max() {
    return this.gebucht + this.angenommen + this.warteliste;
  }

  get buchungsstandText(): string {
    if (!this.buchbar) {
      return 'nicht online buchbar';
    }
    if (this.frei <= 0) {
      return `Nachrückerliste`;
    }
    return `noch ${this.frei} von ${this.gesamt} Plätzen frei`;
  }
}

export class Reise {
  $key: string;
  archiv: boolean;
  beschreibung;
  dateBegin: Date;
  dateEnd: Date;
  dateString: string;
  image: string;
  kurzname: string;
  name: string;
  schlagworte: string;
  capacity: Capacity = null;
  kategorien: Kategorie[] = null;
  kurse: Kurs[] = null;
  kurtaxe: number = null;
  sorgenfreiTarif: number = null;
  vorgangsnummer?: string;
  zustiege: Zustieg[] = null;
  kitestationName: string;
  kitestationUrl: string;
  skigebietName: string;
  skigebietUrl: string;
  unterkunftName: string;
  unterkunftUrl: string;

  constructor(json?: any) {
    if (json) {
      this.fromJson(json);
    }
  }
  fromJson(json: any) {
    for (const key of Object.keys(valueOrEmptyObject(json))) {
      this[key] = json[key];
    }
    this.$key = json.$key;
    this.dateBegin = new Date(this.dateBegin);
    this.dateEnd = new Date(this.dateEnd);
    if (!this.dateString) {
      this.dateString = beginEndDateToString(this.dateBegin, this.dateEnd);
    }
    this.kategorien = firebaseObjectToArray(this.kategorien);
  }

  get beschreibungHTML() {
    return this.beschreibung.join('<br>');
  }

  get kitestationHTML() {
    return this.nameUrlHTML('Kitestation', this.kitestationName, this.kitestationUrl);
  }

  get skigebietHTML() {
    return this.nameUrlHTML('Skigebiet', this.skigebietName, this.skigebietUrl);
  }

  get unterkunftHTML() {
    return this.nameUrlHTML('Unterkunft', this.unterkunftName, this.unterkunftUrl);
  }

  get capacities(): Capacity[] {
    const capacities: Capacity[] = [];
    if (this.capacity) {
      capacities.push(this.capacity);
    }
    this.kategorien.forEach(kategorie => {
      if (kategorie.capacity && !capacities.find(c => c === kategorie.capacity)) {
        capacities.push(kategorie.capacity);
      }
    });
    return capacities;
  }

  get buchungsstandText(): string {
    //const mindestbucherzahl = 30;
    let buchbar = false;
    let frei = 0;
    let gebucht = 0;
    let gesamt = 0;
    this.capacities.forEach(capacity => {
      buchbar = buchbar || capacity.buchbar;
      frei += Math.max(0, capacity.frei);
      gebucht += capacity.gebucht;
      gesamt += capacity.gesamt - capacity.geblockt;
    });
    if (!buchbar) {
      return 'Nicht online buchbar';
    }
    /*
    if (gesamt < mindestbucherzahl) {
      const fehlend = mindestbucherzahl - gesamt;
      return `Mindestbucherzahl noch nicht erreicht! Es fehlen noch ${fehlend} Buchungen.`;
    }
     */
    if (frei <= 0) {
      return `Zur Zeit voll, jetzt auf Nachrückerliste eintragen!`;
    }
    return `Noch ${frei} von ${gesamt} Plätzen frei, jetzt buchen!`;
  }

  get buchenButtonText(): string {
    let buchbar = false;
    let frei = 0;
    this.capacities.forEach(capacity => {
      buchbar = buchbar || capacity.buchbar;
      frei += Math.max(0, capacity.frei);
    });
    if (!buchbar) {
      return 'Nicht online buchbar';
    }
    if (frei <= 0) {
      return `auf Nachrückerliste eintragen (zur Zeit voll)`;
    }
    return `BUCHEN`;
  }

  get buchbar(): boolean {
    return this.capacities.some(capacity => capacity.buchbar);
  }

  get isZustiegAenderbar(): boolean {
    return moment().isBefore(moment(this.dateBegin).subtract(moment.duration(3, 'days')));
  }

  get isStornierbar(): boolean {
    return moment().isBefore(moment(this.dateBegin));
  }

  get isTagesreise(): boolean {
    return this.dateBegin.getTime() === this.dateEnd.getTime();
  }
  get anzahlungProzent(): number {
    return this.isTagesreise ? config.anzahlungTagesReise : config.anzahlungMehrtagesReise;
  }
  get dateBeginAsMoment() {
    return moment(this.dateBegin);
  }

  get kategorienWithoutHidden(): Kategorie[] {
    return this.kategorien
        .filter(x => !x.hidden)
        .sort((a, b) => a.preis - b.preis);
  }

  kategorienFilteredByAlter(geburtsdatum: Date): Kategorie[] {
    const kategorien = this.kategorien.filter(x => !x.hidden && (!x.capacity || x.capacity.buchbar));
    if (!geburtsdatum) {
      return kategorien;
    }
    // entweder einzelne Kategorie mit jgd_jahr oder Kategorien mit erw/jgd/kind
    if (kategorien.find(x => x.jgd_jahr)) {
      const geburtsjahr = moment(geburtsdatum).year();
      const filtered = kategorien
          .filter(x => geburtsjahr >= x.jgd_jahr)
          .sort((a, b) => b.jgd_jahr - a.jgd_jahr);
      if (filtered.length) {
        return [filtered[0]];
      } else {
        return kategorien.filter(x => !x.jgd_jahr);
      }
    } else {
      const maxJugendDate = moment(geburtsdatum).add(config.jugendMaxAlter);
      const maxKindDate = moment(geburtsdatum).add(config.kindMaxAlter);
      return kategorien.filter(kategorie => {
        if (kategorie.erw && maxJugendDate.isAfter(this.dateBegin)) {
          return false;
        } else if (kategorie.jgd && maxJugendDate.isSameOrBefore(this.dateBegin)) {
          return false;
        } else if (kategorie.kind && maxKindDate.isSameOrBefore(this.dateBegin)) {
          return false;
        }
        return true;
      });
    }
  }

  get kurseWithoutNull() {
    if (!this.kurse) {
      return null;
    }
    return this.kurse.filter(k => k != null);
  }

  private nameUrlHTML(titel: string, name: string, url: string) {
    if (!name && !url) {
      return null;
    }
    let html = `${titel}: `;
    if (name && url) {
      html += `<a href="${url}" target="_blank">${name}</a>`;
    } else if (name) {
      html += name;
    } else if (url) {
      html += `<a href="${url}" target="_blank">${url}</a>`;
    }
    return html;
  }
}

export class Reisen {
  reisenList: Reise[] = [];
  reisenMap = {};

  constructor(publicData) {
    this.setJson(publicData);
  }

  private setJson(publicData) {
    const reisen = publicData.reisen;
    const kurse = publicData.kurse;
    const zustiege = publicData.zustiege;
    firebaseObjectToObjectWithKey(kurse);
    firebaseObjectToObjectWithKey(zustiege);
    Object.keys(valueOrEmptyObject(reisen)).forEach(reiseKey => {
      const reiseJson = reisen[reiseKey];
      reiseJson.$key = reiseKey;
      const reise = new Reise(reiseJson);
      if (reiseJson.kurse) {
        reise.kurse = reiseJson.kurse.map(k => {
          assert(kurse[k]);
          return kurse[k];
        });
        reise.kurse.unshift(null); // kurs is optional
      }
      if (reiseJson.zustiege) {
        reise.zustiege = reiseJson.zustiege.map(k => {
          assert(zustiege[k]);
          return zustiege[k];
        });
      }
      if (publicData.capacity) {
        { // capacity über alle Kategorien
          const capacityKey = Capacity.combinedKey(reise, null);
          const capacityJson = publicData.capacity[capacityKey];
          if (capacityJson) {
            reise.capacity = new Capacity(capacityKey, capacityJson);
          }
        }
        { // capacity für einzelne Kategorien
          const capacities = {};
          reise.kategorien.filter(k => k.capacity).forEach(kategorie => {
            const capacityKey = Capacity.combinedKey(reise, kategorie.capacity);
            const capacityJson = publicData.capacity[capacityKey];
            if (!capacities[capacityKey] && capacityJson) {
              capacities[capacityKey] = new Capacity(capacityKey, capacityJson);
            }
            kategorie.capacity = capacities[capacityKey];
          });
        }
      }
      this.reisenList.push(reise);
      this.reisenMap[reiseKey] = reise;
    });
  }

  get capacities(): Capacity[] {
    return this.reisenList
      .filter(reise => !reise.archiv)
      .reduce((previousValue, currentValue) => previousValue.concat(currentValue.capacities), []);
  }
}

export class TeilnehmerCollection {
  teilnehmerList: Person[] = [];
  teilnehmerMap = {};

  constructor(json) {
    Object.keys(valueOrEmptyObject(json)).forEach(key => {
      const teilnehmerJson = json[key];
      const teilnehmer = new Person(key, teilnehmerJson);
      this.teilnehmerList.push(teilnehmer);
      this.teilnehmerMap[key] = teilnehmer;
    });
  }
}

// TODO use interface in EinzelBuchung.toJson()
export interface EinzelBuchungJson {
  $key?: string;
  anfrageDate: string;
  archiv: boolean;
  bucherUid: string;
  faelligerBetrag: number;
  gesamtBetrag: number;
  gesamtBezahlt: number;
  kategorieKey: string;
  kursKey: string;
  reiseKey: string;
  sonderpreis: number;
  status: string; // BuchungStates
  statusChanged: string;
  teilnehmerKey?: string;
  teilnehmerUid?: string;
  zimmerwunsch: string;
  kurtaxe?: number;
  sorgenfreiTarif?: number;
  zustiegKey: string;
}

// Buchung einer Reise für eine Person
export class EinzelBuchung {
  $key: string;
  bucher: Stammdaten;
  readonly teilnehmer: Person;
  readonly reise: Reise;
  // moment
  anfrageDate = null;
  zustieg: Zustieg = null;
  kategorie: Kategorie = null;
  kurs: Kurs = null;
  zimmerwunsch: string = null;
  kurtaxe: number = null;
  sorgenfreiTarif: number = null;
  _status = BuchungStates.anfrage;
  statusChanged = null;
  gesamtBetrag = 0;
  sonderpreis: number = null;
  // wird von processBuchungenAnfrage() gesetzt
  faelligerBetrag = 0;
  // Betrag der vom Konto auf diese Buchung zugeteilt wurde
  _gesamtBezahlt = 0;
  archiv = false;
  // index for sorting
  index: number;

  // temp members for GruppenBuchung
  bereitsGebucht = false;
  selected = true;

  static sortAndAddIndex(buchungen: EinzelBuchung[], sortFunction) {
    buchungen.sort(sortFunction);
    for (let i = 0; i !== buchungen.length; ++i) {
      buchungen[i].index = i + 1;
    }
  }

  static sortByAnfrageDate(a: EinzelBuchung, b: EinzelBuchung) {
    return a.anfrageDate - b.anfrageDate;
  }

  static sortByReiseDateBegin(a: EinzelBuchung, b: EinzelBuchung) {
    return a.reise.dateBegin.getTime() - b.reise.dateBegin.getTime();
  }

  static sortByStatusChanged(a: EinzelBuchung, b: EinzelBuchung) {
    return a.statusChanged - b.statusChanged;
  }

  static fromJson(json: EinzelBuchungJson, reisen: Reisen, bucher: Stammdaten, teilnehmerCollection: TeilnehmerCollection): EinzelBuchung {
    assert(json.bucherUid === bucher.uid);
    const reise = getFromMapChecked(reisen.reisenMap, json.reiseKey, 'reise');
    let teilnehmer;
    if (json.teilnehmerUid) {
      assert(json.teilnehmerUid === bucher.uid);
      teilnehmer = bucher;
    } else if (json.teilnehmerKey) {
      teilnehmer = getFromMapChecked(teilnehmerCollection.teilnehmerMap, json.teilnehmerKey, 'teilnehmer');
    } else {
      assert(false, 'EinzelBuchung.fromJson(): teilnehmer missing');
    }
    const eb = new EinzelBuchung(json.$key, bucher, teilnehmer, reise);
    eb.anfrageDate = json.anfrageDate ? moment(json.anfrageDate) : null;
    eb.zustieg = getFromArrayChecked(reise.zustiege, json.zustiegKey, 'zustieg');
    eb.kategorie = getFromArrayChecked(reise.kategorien, json.kategorieKey, 'kategorie');
    if (json.kursKey) {
      eb.kurs = getFromArrayChecked(reise.kurse, json.kursKey, 'kurs');
    }
    eb.zimmerwunsch = stringTrimmed(json.zimmerwunsch);
    eb.kurtaxe = valueOrNull(json.kurtaxe);
    eb.sorgenfreiTarif = valueOrNull(json.sorgenfreiTarif);
    eb.status = valueOrNull(json.status);
    eb.statusChanged = json.statusChanged ? moment(json.statusChanged) : null;
    eb.faelligerBetrag = valueOrZero(json.faelligerBetrag);
    eb.gesamtBetrag = valueOrZero(json.gesamtBetrag);
    eb.sonderpreis = valueOrNull(json.sonderpreis);
    eb._gesamtBezahlt = valueOrZero(json.gesamtBezahlt);
    eb.archiv = valueOrDefault(json.archiv, false);
    assert(eb.valid);
    return eb;
  }

  constructor($key: string, bucher: Stammdaten, teilnehmer: Person, reise: Reise) {
    this.$key = $key;
    this.reise = reise;
    this.teilnehmer = teilnehmer;
    this.bucher = bucher;
    // auto select if there's only one option
    if (reise.zustiege != null && reise.zustiege.length === 1) {
      this.zustieg = reise.zustiege[0];
    }
    if (reise.kategorien != null && reise.kategorien.length === 1) {
      this.kategorie = reise.kategorien[0];
    }
  }

  toJson() {
    const json: EinzelBuchungJson = {
      anfrageDate: this.anfrageDate ? this.anfrageDate.format() : null,
      bucherUid: this.bucher.uid,
      reiseKey: this.reise.$key,
      zustiegKey: this.zustieg ? this.zustieg.$key : null,
      kategorieKey: this.kategorie ? this.kategorie.$key : null,
      kursKey: this.kurs ? this.kurs.$key : null,
      zimmerwunsch: stringTrimmed(this.zimmerwunsch),
      kurtaxe: this.kurtaxe,
      sorgenfreiTarif: this.sorgenfreiTarif,
      status: this.status,
      statusChanged: this.statusChanged ? this.statusChanged.format() : null,
      faelligerBetrag: this.faelligerBetrag,
      gesamtBetrag: this.gesamtBetrag,
      sonderpreis: this.sonderpreis,
      gesamtBezahlt: this.gesamtBezahlt,
      archiv: valueOrDefault(this.archiv, false)
    };
    if (this.teilnehmer instanceof Stammdaten) {
      json.teilnehmerUid = this.teilnehmer.uid;
    } else {
      json.teilnehmerKey = this.teilnehmer.$key;
    }
    return json;
  }

  get bucherName(): string {
    return this.bucher.name();
  }

  get guidingName(): string {
    return this.kurs ? this.kurs.name : null;
  }

  get kategorieName(): string {
    return this.kategorie ? this.kategorie.name : null;
  }

  get valid(): boolean {
    if (!this.$key == null) {
      return false;
    }
    if (this.bereitsGebucht || !this.selected || this.reise == null || this.teilnehmer == null) {
      return false;
    }
    if (this.reise.zustiege != null && this.zustieg == null) {
      return false;
    }
    if (this.reise.kategorien != null && this.kategorie == null) {
      return false;
    }
    return true;
  }

  get status() {
    return this._status;
  }
  set status(status) {
    this._status = status;
    this.statusChanged = moment();
  }

  get storniertOderAbgesagt() {
    return this._status === BuchungStates.storniert || this._status === BuchungStates.abgesagt || this._status === BuchungStates.abgesagtCorona;
  }

  get teilnehmerGeburtsdatum() {
    return this.teilnehmer.geburtsdatum;
  }

  get teilnehmerName(): string {
    return this.teilnehmer.name();
  }

  get capacity(): Capacity {
    if (this.kategorie && this.kategorie.capacity) {
      return this.kategorie.capacity;
    }
    return this.reise.capacity;
  }

  get defaultBetrag(): number {
    if (typeof this.sonderpreis === 'number') { // not null, but 0 is allowed
      return this.sonderpreis + this.kurtaxe + this.sorgenfreiTarif;
    }
    if (!this.kategorie || !this.kategorie.preis) {
      return 0;
    }
    return this.kategorie.preis + this.kurtaxe + this.sorgenfreiTarif;
  }
  get gesamtBezahlt() {
    return this._gesamtBezahlt;
  }
  get offenerBetrag(): number {
    if (!this.kostenSindEntstanden) {
      return 0;
    }
    return this.gesamtBetrag - this.gesamtBezahlt;
  }
  get anzahlungTermin() {
    return moment(this.reise.dateBegin).subtract(config.anzahlungZeitraum);
  }
  get restzahlungTermin() {
    return moment(this.reise.dateBegin).subtract(config.restzahlungZeitraum);
  }
  updateFaelligerBetrag(now): number {
    const offen = this.offenerBetrag;
    if (offen <= 0) {
      this.faelligerBetrag = 0;
    } else if (now.isBefore(this.anzahlungTermin)
      && this.status !== BuchungStates.storniert) { // Anzahlung noch nicht fällig
      this.faelligerBetrag = 0;
    } else if (this.gesamtBezahlt === 0
      && this.status !== BuchungStates.storniert
      && now.isBefore(this.restzahlungTermin)) { // nur Anzahlung fällig
      this.faelligerBetrag = Math.floor(offen * this.reise.anzahlungProzent / 100);
    } else if (this.gesamtBezahlt !== 0
      && this.status !== BuchungStates.storniert
      && now.isBefore(this.restzahlungTermin)) { // Restzahlung noch nicht fällig
      this.faelligerBetrag = 0;
    } else { // offener Betrag komplett fällig
      this.faelligerBetrag = offen;
    }
    return this.faelligerBetrag;
  }
  get kostenSindEntstanden(): boolean {
    switch (this.status) {
      case BuchungStates.anfrage:
      case BuchungStates.abgelehntNichtBuchbar:
      case BuchungStates.abgelehntAusgebucht:
      case BuchungStates.warteliste:
      case BuchungStates.abgesagt:
        return false;
      case BuchungStates.angenommen:
      case BuchungStates.gebucht:
      case BuchungStates.teilgenommen:
      case BuchungStates.storniert:
      case BuchungStates.abgesagtCorona:
        return true;
      default:
        assert(false, `unexpected BuchungStates: this.status`);
        return false;
    }
  }
  get zahlungHinweisText(): string {
    if (!this.offenerBetrag) {
      return null;
    } else if (this.faelligerBetrag === this.offenerBetrag) {
      return 'Zahlung fällig';
    }
    else if (!this.gesamtBezahlt) {
      if (moment().isBefore(this.anzahlungTermin)) {
        return 'Anzahlung fällig am ' + this.anzahlungTermin.format('L');
      } else {
        return 'Anzahlung fällig';
      }
    } else {
      if (moment().isBefore(this.restzahlungTermin)) {
        return 'Restzahlung fällig am ' + this.restzahlungTermin.format('L');
      } else {
        return 'Restzahlung fällig';
      }
    }
  }

  get zustiegAbk(): string {
    return this.zustieg ? this.zustieg.abk : null;
  }

  get zustiegOrt(): string {
    return this.zustieg ? this.zustieg.ort : null;
  }

  get zustiegZeitOrt(): string {
    return this.zustieg ? `${this.zustieg.zeit} ${this.zustieg.abk}` : null;
  }

  getStornoKosten(now: Date): number {
    if (this.status === BuchungStates.angenommen || this.status === BuchungStates.gebucht || this.status === BuchungStates.teilgenommen) {
      return EinzelBuchung.getStornoKostenForDate(now, this.reise.dateBegin, this.gesamtBetrag - this.kurtaxe - this.sorgenfreiTarif);
    } else {
      return 0;
    }
  }

  static getStornoKostenForDate(now: Date, reiseBegin: Date, betrag: number): number {
    if (betrag === 0) {
      return 0;
    }
    if (moment(now).isBefore(moment(reiseBegin).subtract(config.anzahlungZeitraum))) {
      return config.bearbeitungspauschale;
    }
    // Stornokosten des Reiseveranstalters
    const tage_vor_begin = (reiseBegin.valueOf() - now.valueOf()) / (1000 * 60 * 60 * 24);
    if (tage_vor_begin >= 45) {
      betrag *= 0.10;
    } else if (tage_vor_begin >= 31) {
      betrag *= 0.20;
    } else if (tage_vor_begin >= 15) {
      betrag *= 0.35;
    } else if (tage_vor_begin >= 7) {
      betrag *= 0.55;
    } else if (tage_vor_begin >= 2) {
      betrag *= 0.75;
    } else if (tage_vor_begin >= 0) {
      betrag *= 0.90;
    } else {
      // kein Zugang vor Reisebeginn: 100%
    }
    return betragRound(betrag);
  }

  isStatusChangeSupported(newStatus: string): boolean {
    return this.supportedNewStatus().some(oldStatus => newStatus === oldStatus);
  }

  supportedNewStatus(): string[] {
    switch (this.status) {
      case BuchungStates.anfrage:
        return [
          BuchungStates.warteliste,
          BuchungStates.abgesagt
        ];
      case BuchungStates.abgelehntNichtBuchbar:
        return [
          BuchungStates.warteliste,
          BuchungStates.abgesagt
        ];
      case BuchungStates.abgelehntAusgebucht:
        return [
          BuchungStates.warteliste,
          BuchungStates.abgesagt
        ];
      case BuchungStates.warteliste:
        return [
          BuchungStates.angenommen,
          BuchungStates.gebucht,
          BuchungStates.storniert,
          BuchungStates.abgelehntAusgebucht,
          BuchungStates.abgesagt
        ];
      case BuchungStates.angenommen:
        return [
          BuchungStates.gebucht,
          BuchungStates.storniert,
          BuchungStates.warteliste,
          BuchungStates.abgelehntAusgebucht,
          BuchungStates.abgesagt
        ];
      case BuchungStates.gebucht:
        return [
          BuchungStates.angenommen,
          BuchungStates.teilgenommen,
          BuchungStates.storniert,
          BuchungStates.abgesagt
        ];
      case BuchungStates.teilgenommen:
        return [
          BuchungStates.storniert,
          BuchungStates.abgesagt
        ];
      case BuchungStates.storniert:
        return [
          BuchungStates.warteliste,
          BuchungStates.angenommen,
          BuchungStates.gebucht,
          BuchungStates.teilgenommen,
          BuchungStates.abgelehntAusgebucht,
          BuchungStates.abgesagt
        ];
      case BuchungStates.abgesagt:
      case BuchungStates.abgesagtCorona:
        return [
          BuchungStates.warteliste,
          BuchungStates.angenommen,
          BuchungStates.gebucht,
          BuchungStates.teilgenommen,
          BuchungStates.abgelehntAusgebucht
        ];
      default:
        assert(false, `unexpected BuchungStates: this.status`);
        return [];
    }
  }
}

export class RechnungItems {
  buchungen: EinzelBuchung[];
  gesamtBetrag = 0;
  gesamtBezahlt = 0;
  offenerBetrag = 0;
  faelligerBetrag = 0;
  mehrereTeilnehmer = false;
  kurseGebucht = false;
  jgdOderKind = false;
  sonderpreis = false;

  constructor(buchungen: EinzelBuchung[]) {
    this.buchungen = buchungen;
    let teilnehmner = null;
    buchungen.forEach(eb => {
      const ebGesamtBetrag = eb.gesamtBetrag;
      const ebGesamtBezahlt = eb.gesamtBezahlt;
      const ebOffenerBetrag = eb.offenerBetrag;
      this.gesamtBetrag += ebGesamtBetrag ? ebGesamtBetrag : 0;
      this.gesamtBezahlt += ebGesamtBezahlt ? ebGesamtBezahlt : 0;
      this.offenerBetrag += ebOffenerBetrag ? ebOffenerBetrag : 0;
      this.faelligerBetrag += eb.faelligerBetrag ? eb.faelligerBetrag : 0;
      if (!teilnehmner) {
        teilnehmner = eb.teilnehmer;
      } else if (teilnehmner !== eb.teilnehmer) {
        this.mehrereTeilnehmer = true;
      }
      if (eb.kurs) {
        this.kurseGebucht = true;
      }
      if (eb.kategorie.jgd || eb.kategorie.kind || eb.kategorie.jgd_jahr) {
        this.jgdOderKind = true;
      }
      if (typeof eb.sonderpreis === 'number') {
        this.sonderpreis = true;
      }
    });
  }
}

export class Rechnung {
  alleItems: RechnungItems;
  // Buchungen bei denen Kosten entstanden sind
  buchungenMitKosten: RechnungItems;
  offeneItems: RechnungItems;

  constructor(buchungen: EinzelBuchung[]) {
    buchungen.sort(EinzelBuchung.sortByReiseDateBegin);
    this.alleItems = new RechnungItems(buchungen);
    this.buchungenMitKosten = new RechnungItems(buchungen.filter(eb => eb.kostenSindEntstanden));
    this.offeneItems = new RechnungItems(buchungen.filter(eb => eb.kostenSindEntstanden && eb.offenerBetrag));
  }
}

export class Zahlung {
  $key: string;
  id: string;
  art: string;
  betrag: number;
  fee: number;
  date: Date;
  archiv = false;

  static sortByDate(a: Zahlung, b: Zahlung) {
    return a.date.getTime() - b.date.getTime();
  }

  fromJson(json) {
    Object.assign(this, json);
    this.betrag = Number.parseFloat(json.betrag);
    this.fee = json.fee ? Number.parseFloat(json.fee) : 0;
    this.date = new Date(json.date);
    return this;
  }
}

export interface ZahlungJson {
  art: string;
  betrag: number;
  date: string;
  fee: number;
  id: string;
}

export interface LogJson {
  date: string;
  authUser: string;
  kunde: string;
  reise: string;
  event: string;
  key: string;
  value: any;
}
